import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Table, Badge, Spinner, Alert } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { NotificationContainer, NotificationManager } from "react-notifications";
import { getQueryPostDto, getQuery } from "../ServiceController";
import { PageHeader, FilterCard, SimpleCard, SummaryCard } from "@gull";
import { formattaDataOraHHmmss } from "app/utility/dataOra";
import VisualizzaRicettaModal from "./VisualizaRicettaModal";
import { ricercaErogato } from "./nreService";
import { ElencoErroriRicetta } from "./ElencoErroriRicetta";

const getLocalTodayIso = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const displayDate = value => value && Number.isFinite(Date.parse(value)) ? formattaDataOraHHmmss(value) : "—";

const GestioneRicetteNre = () => {
  const dispatch = useDispatch();

  const todayIso = useMemo(() => getLocalTodayIso(), []);

  const [isLoading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [registryLoading, setRegistryLoading] = useState(true);
  const [registryError, setRegistryError] = useState(false);
  const [responseVisualizza, setResponseVisualizza] = useState();
  const [show, setShow] = useState(false);
  const [erroriRicetta, setErroriRicetta] = useState(null);

  // filtri
  const [registri, setRegistri] = useState([]);
  const [idRegistro, setIdRegistro] = useState(0);

  // ✅ inizializzate con data odierna
  const [dal, setDal] = useState(todayIso);
  const [al, setAl] = useState(todayIso);

  const [nre, setNre] = useState("");
  const [statoRicetta, setStatoRicetta] = useState("E"); // obbligatorio (P/E) quando NON si fa ricerca puntuale NRE
  const [tipoData, setTipoData] = useState("E"); // E=erogazione, T=trasmissione (quando NON si fa ricerca puntuale NRE)

  // risultati
  const [records, setRecords] = useState([]);

  // ---- badge mapping (stati numerici) ----
  const badgeMap = useMemo(
    () => ({
      3: { cls: "bg-secondary", label: "DA EROGARE" },
      4: { cls: "bg-danger", label: "ANNULLATA DAL PRESCRITTORE" },
      5: { cls: "bg-primary", label: "PRESA IN CARICO" },
      6: { cls: "bg-warning text-dark", label: "SOSPESA" },
      7: { cls: "bg-primary", label: "SINGOLA PRESCRIZIONE EROGATA" },
      8: { cls: "bg-success", label: "RICETTA EROGATA" },
      9: { cls: "bg-dark", label: "RI-EROGATA DOPO ANNULLAMENTO" },
    }),
    []
  );

  const getBadgeStato = (statoNumerico) => {
    const key = Number(statoNumerico);
    const info = badgeMap[key] || { cls: "bg-light text-dark", label: "N/D" };

    return (
      <span>
        <span className={`badge ${info.cls} p-2 me-2`}>
          {Number.isFinite(key) ? key : "?"}
        </span>
        {info.label}
      </span>
    );
  };

  // --- helpers date ---
  const parseIsoDate = (s) => {
    if (!s) return null;
    const d = new Date(`${s}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const daysDiffInclusive = (d1, d2) => {
    const ms = d2.getTime() - d1.getTime();
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    return days + 1;
  };

  const isSameMonth = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();

  const clearNotifications = () => {
    NotificationManager.listNotify?.forEach((n) =>
      NotificationManager.remove({ id: n.id })
    );
  };

  // ✅ NRE valido SOLO se lungo 15 caratteri
  const nreTrim = (nre || "").trim();
  const hasNre = nreTrim.length > 0;
  const isNreValid = !hasNre || nreTrim.length === 15;

  /**
   * ✅ La ricerca può partire SOLO in questi 3 casi:
   * 1) tutti i filtri valorizzati (incl. NRE valido)
   * 2) tutti i filtri tranne NRE valorizzati
   * 3) solo NRE (valido 15) e idRegistro valorizzati (ricerca puntuale)
   *
   * Nota: quando fai ricerca puntuale (caso 3) non obblighiamo DAL/AL/stato/tipo.
   */
  const validateFiltri = () => {
    // registro sempre necessario
    if (!Number(idRegistro) || Number(idRegistro) <= 0) {
      return {
        error: "Seleziona un Registro valido prima di effettuare la ricerca.",
        mode: null,
      };
    }

    // Se NRE valorizzato => deve essere lungo 15
    if (hasNre && nreTrim.length !== 15) {
      return {
        error: `NRE non valido: deve essere lungo ESATTAMENTE 15 caratteri (attuali: ${nreTrim.length}).`,
        mode: null,
      };
    }

    const hasDal = !!dal;
    const hasAl = !!al;

    // --- Caso 3: solo NRE + registro (ricerca puntuale) ---
    // Permettiamo la ricerca anche se date/stato/tipo non sono valorizzati.
    if (hasNre && (!hasDal || !hasAl)) {
      return {
        error: null,
        mode: "NRE_ONLY",
        info:
          "Ricerca puntuale per NRE: verranno ignorati i filtri data/stato/tipo (se non valorizzati).",
      };
    }

    // --- Caso 1 e 2: ricerca per intervallo (date obbligatorie) ---
    if (!hasDal || !hasAl) {
      return {
        error:
          "DAL e AL sono obbligatori per la ricerca per intervallo. In alternativa, compila SOLO NRE (15 caratteri) per la ricerca puntuale.",
        mode: null,
      };
    }

    if (!statoRicetta || (statoRicetta !== "P" && statoRicetta !== "E")) {
      return {
        error:
          'statoRicetta non valido: usare "P" (prese in carico) o "E" (erogazione terminata).',
        mode: null,
      };
    }

    if (!tipoData || (tipoData !== "E" && tipoData !== "T")) {
      return {
        error: 'tipoData non valido: usare "E" (erogazione) o "T" (trasmissione).',
        mode: null,
      };
    }

    const dDal = parseIsoDate(dal);
    const dAl = parseIsoDate(al);
    if (!dDal || !dAl) {
      return { error: "Formato date non valido.", mode: null };
    }
    if (dDal.getTime() > dAl.getTime()) {
      return { error: "DAL non può essere maggiore di AL.", mode: null };
    }

    // regole:
    // - stesso mese: qualsiasi intervallo
    // - a cavallo tra mesi: max 7 giorni inclusivi
    if (!isSameMonth(dDal, dAl)) {
      const diffInc = daysDiffInclusive(dDal, dAl);
      if (diffInc > 7) {
        return {
          error:
            "Per periodi a cavallo tra due mesi, l'intervallo massimo consentito è 7 giorni (inclusivi).",
          mode: null,
        };
      }
    }

    return {
      error: null,
      mode: hasNre ? "ALL_FILTERS" : "ALL_EXCEPT_NRE",
      info: hasNre
        ? "Ricerca con tutti i filtri: intervallo + NRE puntuale."
        : "Ricerca con filtri completi (senza NRE): intervallo + stato + tipo data.",
    };
  };

  const resetFiltri = () => {
    setDal(todayIso);
    setAl(todayIso);
    setNre("");
    setStatoRicetta("P");
    setTipoData("E");
    setRecords([]);
    setSearched(false);
    setSearchError(false);
    setErroriRicetta(null);
  };

  const buildPayload = (overrideNre) => {
    const finalNre = (overrideNre ?? nreTrim).trim();

    return {
      dal: dal || null,
      al: al || null,
      idRegistro: Number(idRegistro) || 0,
      nre: finalNre,
      statoRicetta: statoRicetta || null,
      tipoData: tipoData || null,
    };
  };

  const handleRicerca = async () => {
    clearNotifications();

    const v = validateFiltri();
    if (v?.error) {
      NotificationManager.warning(v.error, "Ricerca non avviata");
      return;
    }

    setSearched(true);
    setSearchError(false);
    setErroriRicetta(null);
    const payload = buildPayload();

    setLoading(true);
    try {
      const res = await dispatch(ricercaErogato(payload));

      if (!res?.isSuccess) {
        // NotificationManager.error(
        //   res?.ui?.userMessage || res?.message || "Errore durante la ricerca",
        //   "Errore"
        // );
        setRecords([]);
        setErroriRicetta(res);
        setSearchError(true);
        return;
      }

      const data = res?.data?.elencoRicette;
      setRecords(Array.isArray(data) ? data : []);
    } catch (e) {
      // qui restano solo errori davvero “inaspettati”
      console.error(e);
      setSearchError(true);
      NotificationManager.error("Errore durante la chiamata al server", "Errore");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };


  // Visualizza (POST con NRE)
  const handleVisualizza = async (codiceNre) => {
    clearNotifications();

    const codice = (codiceNre || "").trim();
    if (!codice) {
      NotificationManager.warning("NRE mancante", "Attenzione");
      return;
    }
    if (codice.length !== 15) {
      NotificationManager.warning(
        `NRE non valido: deve essere lungo ESATTAMENTE 15 caratteri (attuali: ${codice.length}).`,
        "Attenzione"
      );
      return;
    }
    if (!Number(idRegistro) || Number(idRegistro) <= 0) {
      NotificationManager.warning("Seleziona un Registro valido.", "Attenzione");
      return;
    }

    const payload = buildPayload(codice);

    setLoading(true);
    try {
      const res = await dispatch(
        getQueryPostDto("Nre/VisualizzaRicettaDaRicerca", payload)
      );
      if (!res?.isSuccess) {
        NotificationManager.error(
          res?.message || "Errore durante la visualizzazione",
          "Errore"
        );
        return;
      }

      setResponseVisualizza(res?.data);
      setShow(true);
      console.log("Dettaglio ricetta:", res?.data);

      //NotificationManager.success("Dettaglio ricetta caricato", "OK");
    } catch (e) {
      console.error(e);
      NotificationManager.error("Errore chiamata server", "Errore");
    } finally {
      setLoading(false);
    }
  };

  // carico registri
  useEffect(() => {
    dispatch(getQuery("RegistriIva/ElencoRegistriIva"))
      .then((data) => {
        const list = data || [];
        setRegistri(list);
        if (list.length > 0) setIdRegistro(Number(list[0].idregistro));
      })
      .catch((err) => {
        setRegistryError(true);
        console.error("Errore nel caricamento dei registri:", err);
        NotificationManager.error(
          "Errore nel caricamento dei registri IVA",
          "Errore"
        );
      }).finally(() => setRegistryLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // colonne (in stile RiepilogoNre: array + map su th)
  const colonneFisse = [
    { key: "__progressivo", label: "#", width: 60 },
    { key: "nre", label: "NRE" },
    { key: "__statoBadge", label: "Stato ricetta" },
    { key: "dataPresaCarico", label: "Data presa in carico" },
    { key: "dataErogazione", label: "Data erogazione" },
    { key: "__azioni", label: "Azioni" },
  ];

  const handleClose = () => {
    setShow(false);
    setResponseVisualizza();
  };

  return <div className="gull-workspace-page">
    <PageHeader routeSegments={[{ name: "Contabilità", path: "/contabilita" }, { name: "Gestione ricette NRE" }]}
      subtitle="Ricerca le ricette, verifica lo stato di erogazione e consulta il dettaglio." />
    {registryError && <Alert variant="danger">Impossibile caricare i registri IVA. Ricarica la pagina per riprovare.</Alert>}
    <FilterCard title="Ricerca ricette" subtitle="Scegli il registro e il periodo, oppure cerca una ricetta tramite NRE." icon={<i className="i-Filter-2 text-primary" aria-hidden="true" />} className="border-top border-3 border-primary mb-4">
      <Form onSubmit={event => { event.preventDefault(); handleRicerca(); }}>
        <fieldset disabled={isLoading || registryLoading || registryError}>
          <div className="row">
            <div className="col-md-6 col-xl-4 mb-3"><Form.Group controlId="nre-registro"><Form.Label>Registro IVA *</Form.Label><Form.Select className="form-control" value={idRegistro} onChange={event => setIdRegistro(Number(event.target.value))}><option value={0}>{registryLoading ? "Caricamento registri…" : "Seleziona un registro"}</option>{registri.map(r => <option key={r.idregistro} value={r.idregistro}>{r.idregistro} - {r.descrizione}</option>)}</Form.Select></Form.Group></div>
            <div className="col-md-6 col-xl-4 mb-3"><Form.Group controlId="nre-codice"><Form.Label>NRE</Form.Label><Form.Control value={nre} maxLength={15} placeholder="15 caratteri" onChange={event => setNre(event.target.value)} isInvalid={hasNre && !isNreValid} aria-describedby="nre-feedback" /><Form.Control.Feedback id="nre-feedback" type="invalid">Inserisci esattamente 15 caratteri (attuali: {nreTrim.length}).</Form.Control.Feedback></Form.Group></div>
            <div className="col-md-6 col-xl-2 mb-3"><Form.Group controlId="nre-dal"><Form.Label>Data inizio</Form.Label><Form.Control type="date" value={dal} onChange={event => setDal(event.target.value)} /></Form.Group></div>
            <div className="col-md-6 col-xl-2 mb-3"><Form.Group controlId="nre-al"><Form.Label>Data fine</Form.Label><Form.Control type="date" value={al} onChange={event => setAl(event.target.value)} /></Form.Group></div>
          </div>
          <div className="bg-light rounded border p-3 small mb-3">
            <p className="mb-1"><strong>Ricerca per periodo:</strong> nello stesso mese puoi scegliere qualsiasi intervallo; tra mesi diversi il limite è 7 giorni inclusivi.</p>
            <p className="mb-0"><strong>Ricerca puntuale:</strong> inserisci registro e NRE e svuota i campi data. Con le date compilate vengono applicati anche i filtri del periodo.</p>
          </div>
          <div className="gull-filter-actions"><Button variant="outline-secondary" onClick={resetFiltri}>Azzera filtri</Button><Button type="submit"><i className="i-Magnifi-Glass1" aria-hidden="true" />Ricerca ricette</Button></div>
        </fieldset>
      </Form>
    </FilterCard>
    <div className="row g-3 mb-4" aria-label="Riepilogo dei risultati">
      {[
        {label: "Ricette trovate", value: records.length, icon: "i-Receipt-4", variant: "primary"},
        {label: "Erogate o ri-erogate", value: records.filter(r => [8,9].includes(Number(r.statoRicetta))).length, icon: "i-Check", variant: "success"},
        {label: "Prese in carico o sospese", value: records.filter(r => [5,6].includes(Number(r.statoRicetta))).length, icon: "i-Clock", variant: "warning"},
      ].map(card => <div key={card.label} className="col-sm-4"><SummaryCard label={card.label} value={!searched || isLoading || searchError ? "—" : card.value} icon={<i className={card.icon} />} variant={card.variant} /></div>)}
    </div>
    <SimpleCard className="gull-results-card">
      <div className="gull-results-header"><div><h3 className="mb-1">Elenco ricette</h3><span className="small text-muted">Consulta lo stato e apri il dettaglio della ricetta.</span></div>{searched && !isLoading && !searchError && <Badge bg="light" text="primary" className="border">{records.length} risultati</Badge>}</div>
      <div className="table-responsive gull-table-wrap" aria-busy={isLoading}>
        <Table hover className="mb-0"><caption className="visually-hidden">Ricette NRE risultanti dalla ricerca</caption>
          <thead><tr>{colonneFisse.map(column => <th key={column.key} scope="col">{column.label}</th>)}</tr></thead>
          <tbody>{isLoading ? <tr><td colSpan={6} className="p-5 text-center text-muted"><span role="status"><Spinner size="sm" animation="border" className="me-2" />Caricamento…</span></td></tr>
            : searchError ? <tr><td colSpan={6} className="p-5 text-center text-muted">Ricerca non completata. Verifica gli errori segnalati e riprova.</td></tr>
              : !records.length ? <tr><td colSpan={6} className="p-5 text-center text-muted">{searched ? "Nessuna ricetta trovata con i filtri selezionati." : "Imposta i filtri e avvia la ricerca per visualizzare le ricette."}</td></tr>
                : records.map((r, index) => <tr key={`${r.nre || "ND"}-${index}`}>
                  <td className="text-muted">{index + 1}</td><td className="fw-semibold text-primary text-nowrap">{r.nre || "—"}</td><td>{getBadgeStato(r.statoRicetta)}</td>
                  <td className="text-nowrap">{displayDate(r.dataPresaCarico)}</td><td className="text-nowrap">{displayDate(r.dataErogazione)}</td>
                  <td><Button size="sm" variant="outline-primary" onClick={() => handleVisualizza(r.nre)} disabled={!r.nre || String(r.nre).trim().length !== 15} title="Visualizza dettaglio ricetta"><i className="i-Eye" aria-hidden="true" />Visualizza</Button></td>
                </tr>)}</tbody>
        </Table>
      </div>
    </SimpleCard>
    <NotificationContainer />
    {responseVisualizza && <VisualizzaRicettaModal show={show} response={responseVisualizza} onClose={handleClose} onRicarica={handleRicerca} />}
    {erroriRicetta && ElencoErroriRicetta(erroriRicetta, {title: "Errori ricerca", onClose: () => setErroriRicetta(null)})}
  </div>;
};

export default GestioneRicetteNre;
